// The Oil-Stone Scepter (§31 A4): play an enemy faction's turn.
//
// "As long as both are intact, you may select an enemy faction to control for
// one full round every five rounds, including on the round they are placed."
// Both = the Sacred Well (`WELL`) and its Scepter on a Monarch — `hasPower`.
//
// **What is seized is the target's next turn** — its one turn of the round,
// which is the whole of its round. `seize` records it (`run.seizure`); the
// reducer never asks who is acting, so nothing else changes in the rules: the
// seized side's moves are its own legal moves (it cannot take its own pieces)
// and its ticks run as ever. What reads `seizedBy` is who *plays*: the solo
// opponent driver stands aside, and the session lets the seizer act for the
// side on the clock. **Machine sides never seize** — the planner is blind to it.
//
// Free, turn-bound, a player's act, run-only. "Skip turn" is End Turn (`pass`):
// the doc offers it only on modes with turn timers (Q9), and none has one yet.

import type { Side } from "../board";
import type { GameState } from "../commands";
import { areEnemies, livingSides } from "../position";
import { RULES } from "../rules";
import { cloneState, IllegalCommandError, requireBuilder } from "../reduce";
import { hasPower } from "./powers";

export type SeizeError = "not-a-run" | "no-power" | "not-an-enemy" | "too-soon";

/** Why `owner` may not seize `target` now (the clock aside — `requireBuilder` says that), or null. */
export function seizeError(state: GameState, owner: Side, target: Side): SeizeError | null {
  const run = state.run;
  if (!run) return "not-a-run";
  if (!hasPower(state.industry, owner, "oil-stone")) return "no-power";
  const pos = state.position;
  if (target === owner || !livingSides(pos).includes(target) || !areEnemies(pos, owner, target)) {
    return "not-an-enemy";
  }
  const last = run.seizure;
  if (last && last.by === owner && pos.round < last.at + RULES.seizeEvery) return "too-soon";
  return null;
}

/** The round `owner`'s next seizure may come in, or the current one when it is free. */
export function nextSeizeRound(state: GameState, owner: Side): number {
  const last = state.run?.seizure;
  const round = state.position.round;
  return last && last.by === owner ? Math.max(round, last.at + RULES.seizeEvery) : round;
}

const SEIZE_REFUSAL: Record<SeizeError, string> = {
  "not-a-run": "not a run",
  "no-power": "you hold no Oil-Stone Scepter with its Sacred Well standing",
  "not-an-enemy": "seize a living enemy faction",
  "too-soon": `the Scepter seizes once every ${RULES.seizeEvery} rounds`
};

/** `seize`: record that `owner` plays `target`'s next turn. */
export function applySeize(state: GameState, command: { owner: Side; target: Side }): GameState {
  const owner = requireBuilder(state, command.owner);
  const why = seizeError(state, owner, command.target);
  if (why) throw new IllegalCommandError(SEIZE_REFUSAL[why]);
  const round = state.position.round;
  // Turns run in ascending side order within a round (§31 T1): a target after
  // the seizer still has this round's turn ahead; one before it, the next.
  const at = command.target > owner ? round : round + 1;
  const next = cloneState(state);
  next.run = { ...next.run!, seizure: { by: owner, target: command.target, round: at, at: round } };
  return next;
}

/**
 * Who plays `side`'s turn now in its place, or undefined: the seizer, while
 * the seized turn is on the clock and its Well and Scepter still stand.
 */
export function seizedBy(state: GameState, side: Side = state.position.turn): Side | undefined {
  const seizure = state.run?.seizure;
  if (!seizure || seizure.target !== side || side !== state.position.turn) return undefined;
  if (seizure.round !== state.position.round) return undefined;
  if (!hasPower(state.industry, seizure.by, "oil-stone")) return undefined;
  return seizure.by;
}
